import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Alert,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  SportsBasketball as BasketballIcon,
  SportsBaseball as BaseballIcon,
  SportsFootball as FootballIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { fetchEvents } from '../../services/oddsApi';
import { PROPS_SPORTS_LIST } from '../../utils/propConstants';
import BackButton from '../BackButton';

const SPORT_ICONS = {
  basketball_nba: <BasketballIcon fontSize="small" />,
  americanfootball_nfl: <FootballIcon fontSize="small" />,
  baseball_mlb: <BaseballIcon fontSize="small" />,
};

const formatGameTime = (commenceTime) => {
  const date = new Date(commenceTime);
  const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${dateStr} · ${timeStr}`;
};

const GameCardSkeleton = () => (
  <Card sx={{ mb: 1.5 }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Skeleton width={80} height={20} />
        <Skeleton width={100} height={20} />
      </Box>
      <Skeleton width="60%" height={24} sx={{ mb: 0.5 }} />
      <Skeleton width={40} height={16} sx={{ mb: 0.5 }} />
      <Skeleton width="55%" height={24} />
    </CardContent>
  </Card>
);

const PropsGameListScreen = () => {
  const navigate = useNavigate();
  const [selectedSportIndex, setSelectedSportIndex] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectedSport = PROPS_SPORTS_LIST[selectedSportIndex];

  useEffect(() => {
    if (!selectedSport.enabled) {
      setEvents([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchEvents(selectedSport.value);
        if (!cancelled) {
          setEvents(data || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading events:', err);
          setError('Failed to load games. Please try again.');
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEvents();
    return () => { cancelled = true; };
  }, [selectedSportIndex]);

  const handleGameClick = (event) => {
    navigate(`/player-props/${selectedSport.value}/${event.id}`, {
      state: { event },
    });
  };

  const handleTabChange = (_, newIndex) => {
    setSelectedSportIndex(newIndex);
  };

  return (
    <>
      <BackButton />
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Player Props
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a game to view player prop odds across sportsbooks.
        </Typography>
      </Box>

      {/* Sport Tabs */}
      <Tabs
        value={selectedSportIndex}
        onChange={handleTabChange}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {PROPS_SPORTS_LIST.map((sport, idx) => (
          <Tab
            key={sport.value}
            label={sport.label}
            icon={SPORT_ICONS[sport.value]}
            iconPosition="start"
            disabled={!sport.enabled}
            sx={{ minHeight: 48, textTransform: 'none', fontWeight: 600 }}
          />
        ))}
      </Tabs>

      {/* Coming soon for disabled sports */}
      {!selectedSport.enabled && (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {selectedSport.label} Props Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Player props for {selectedSport.label} are not yet available.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && selectedSport.enabled && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && selectedSport.enabled && (
        <Box>
          {[...Array(5)].map((_, i) => (
            <GameCardSkeleton key={i} />
          ))}
        </Box>
      )}

      {/* Game list */}
      {!loading && selectedSport.enabled && !error && (
        <>
          {events.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No upcoming {selectedSport.label} games
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check back later for upcoming games with available player props.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {events.map((event) => (
                <Card key={event.id} sx={{ '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}>
                  <CardActionArea onClick={() => handleGameClick(event)}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      {/* Meta row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Chip
                          icon={SPORT_ICONS[event.sport_key]}
                          label={event.sport_title}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatGameTime(event.commence_time)}
                        </Typography>
                      </Box>

                      {/* Teams */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {event.away_team}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 0.25 }}>
                            @
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {event.home_team}
                          </Typography>
                        </Box>
                        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}
    </>
  );
};

export default PropsGameListScreen;
